import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { useAvailableGPTModels } from "@/hooks/use-config-query"
import { cn } from "@/lib/utils"
import { KNOWN_SESSION_TYPES } from "@/types"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { FlowPromptSelector } from "../flow-prompt-selector"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { WorkflowModelPicker } from "../workflow-model-picker"
import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"

export function LLMNode({ id, data, selected }: NodeProps) {
  // Flow model choices must match the AI Explorer chat composer, including
  // requiredModules/blockedModules filtering from /api/config.
  const {
    isError: modelsError,
    isLoading: modelsLoading,
    models,
  } = useAvailableGPTModels(KNOWN_SESSION_TYPES.Explorer)
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleSystemPromptChange = useCallback(
    (value: string) => {
      updateNodeData(id, { systemPrompt: value })
    },
    [id, updateNodeData]
  )

  const handlePromptSelect = useCallback(
    (content: string) => {
      updateNodeData(id, { systemPrompt: content })
    },
    [id, updateNodeData]
  )

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleShowInActivityHistoryChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showInActivityHistory: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs">System Prompt</Label>
          <FlowPromptSelector onPromptSelect={handlePromptSelect} />
        </div>
        <TemplateField
          multiline
          placeholder="You are a helpful assistant..."
          value={(data.systemPrompt as string) || ""}
          className={cn(
            "nowheel overflow-auto pr-8",
            isModal ? "min-h-[360px]" : "max-h-[100px] min-h-[60px]"
          )}
          onChange={handleSystemPromptChange}
        />
      </div>
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
      <div className="grid gap-1.5">
        <Label className="text-xs">Model</Label>
        <WorkflowModelPicker
          allowMaxEffort={false}
          availableModels={models}
          hasCatalogError={modelsError}
          isCatalogLoading={modelsLoading}
          modelId={data.model}
          reasoningEffort={data.reasoningEffort}
          onChange={(patch) => updateNodeData(id, patch)}
        />
      </div>
      <div className="grid gap-2 border-t pt-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!data.showOutput}
            className="nodrag"
            id={`${id}-showOutput${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowOutputChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showOutput${isModal ? "-modal" : ""}`}
          >
            Show Output
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={data.showInActivityHistory !== false}
            className="nodrag"
            id={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowInActivityHistoryChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
          >
            Show in Activity History
          </Label>
        </div>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[250px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-3", CATEGORY_TONES[1])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm font-medium">LLM Processor</CardTitle>
            <NodeEditDialog
              description="Edit this node in a larger workspace."
              title="LLM Processor"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </div>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle position={Position.Left} type="target" />
          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
